<template>
    <div>
        <app-bar :title="t('advices_title')">
            <template #actions>
                <div class="px-10">
                    <v-btn
                        :to="{ name: 'advices.create' }"
                        class="sm sm-h"
                        color="orange"
                    >{{ t('advice_title_add') }}</v-btn>
                </div>
            </template>
        </app-bar>

        <v-layout>
            <v-progress-linear
                v-if="overlay"
                absolute
                indeterminate
            ></v-progress-linear>

            <v-tabs
                class="px-4 my-7"
                height="52"
            >
                <div class="tabs-line"></div>
                <v-tab
                    :to="{ name: 'advice-sections' }"
                    class="pl-0"
                >{{ t('advices_tab_sections') }}</v-tab>
                <v-tab
                    :to="{ name: 'advices' }"
                >{{ t('advices_tab_advices') }}</v-tab>
                <v-tabs-items>
                    <card-collection
                        class="px-0-nested"
                        :meta="meta"
                        :busy="overlay"
                        @updated="updated"
                    >
                        <template #filters>
                            <v-toolbar
                                elevation="0"
                                color="transparent"
                                class="mt-7"
                                height="56"
                            >
                                <v-text-field
                                    v-model="queries.search"
                                    @click:clear="clear"
                                    class="input-search md mr-2"
                                    clearable
                                    outlined
                                    dense
                                    hide-details
                                    prepend-inner-icon="mdi-magnify"
                                    :label="t('general_label_search')"
                                ></v-text-field>

                                <v-spacer></v-spacer>

                                <v-select
                                    v-model="queries.advice_section_id"
                                    :items="sections"
                                    class="md mr-2"
                                    outlined
                                    dense
                                    append-icon="mdi-chevron-down"
                                    hide-details
                                    :label="t('advice_label_section')"
                                ></v-select>

                                <v-spacer></v-spacer>

                                <v-select
                                    v-model="queries.published"
                                    :items="[ { text: '-', value: '' }, { text: t('general_text_yes'), value: '1' }, { text: t('general_text_no'), value: '0' } ]"
                                    class="md mr-2"
                                    outlined
                                    dense
                                    append-icon="mdi-chevron-down"
                                    hide-details
                                    :label="t('advice_label_published')"
                                ></v-select>

                                <span class="sub-label px-5">{{ t('general_label_date_period') }}</span>

                                <date-picker-modal
                                    :value="queries.date_from"
                                    @updated="(v) => queries.date_from = v"
                                >
                                    <template #default="{ on, attrs }">
                                        <div
                                            v-on="on"
                                            v-bind="attrs"
                                        >
                                            <v-text-field
                                                v-model="queries.date_from"
                                                class="xs input-color-inherit"
                                                outlined
                                                dense
                                                hide-details
                                                readonly
                                                :label="t('general_label_from')"
                                            ></v-text-field>
                                        </div>
                                    </template>
                                </date-picker-modal>

                                <span class="sub-label px-2">-</span>

                                <date-picker-modal
                                    :value="queries.date_to"
                                    @updated="(v) => queries.date_to = v"
                                >
                                    <template #default="{ on, attrs }">
                                        <div
                                            v-on="on"
                                            v-bind="attrs"
                                        >
                                            <v-text-field
                                                v-model="queries.date_to"
                                                class="xs input-color-inherit"
                                                outlined
                                                dense
                                                hide-details
                                                readonly
                                                :label="t('general_label_to')"
                                            ></v-text-field>
                                        </div>
                                    </template>
                                </date-picker-modal>

                                <v-spacer></v-spacer>

                                <v-btn
                                    @click="search"
                                    class="sm mx-5"
                                    color="primary"
                                >{{ t('general_action_search') }}</v-btn>

                                <v-spacer></v-spacer>

                                <v-btn
                                    v-ripple="false"
                                    plain
                                    color="orange darken-4"
                                    @click="reset"
                                >{{ t('general_action_reset_filters') }}</v-btn>

                            </v-toolbar>
                        </template>

                        <template #data>
                            <thead>
                            <tr>
                                <th class="touched">
                                    <sort-field
                                        v-model="queries.sort"
                                        field="id"
                                        @touched="onSort"
                                    >{{ t('advice_label_id') }}
                                    </sort-field>
                                </th>
                                <th>
                                    <sort-field
                                        v-model="queries.sort"
                                        field="name"
                                        @touched="onSort"
                                    >{{ t('advice_label_name') }}
                                    </sort-field>
                                </th>
                                <th>{{ t('advice_label_section') }}</th>
                                <th>
                                    <sort-field
                                        v-model="queries.sort"
                                        field="created_at"
                                        @touched="onSort"
                                    >{{ t('general_label_creation_date') }}
                                    </sort-field>
                                </th>
                                <th>
                                    <sort-field
                                        v-model="queries.sort"
                                        field="published"
                                        @touched="onSort"
                                    >{{ t('advice_label_published') }}
                                    </sort-field>
                                </th>
                                <th>
                                    <sort-field
                                        v-model="queries.sort"
                                        field="sort_order"
                                        @touched="onSort"
                                    >{{ t('advice_section_label_sort_order') }}
                                    </sort-field>
                                </th>
                                <th class="text-center">{{ t('general_label_action') }}</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-if="collection.data.length === 0">
                                <td class="text-center" colspan="7">{{ t('general_text_no_data') }}</td>
                            </tr>
                            <template v-else v-for="advice in collection.data">
                                <tr>
                                    <td>
                                        <a
                                            :href="`/app-contents/advices/advices/${advice.id}/view`"
                                            @click.prevent="touch(advice)"
                                            :class="{ 'orange--text': ! advice.published }"
                                        >{{ advice.id }}</a>
                                    </td>
                                    <td>
                                        <a
                                            :href="`/app-contents/advices/advices/${advice.id}/view`"
                                            @click.prevent="touch(advice)"
                                        >{{ advice.name }}</a>
                                    </td>
                                    <td>{{ advice.section.name }}</td>
                                    <td>
                                        {{ advice.createdAt.format('DD/MM/YYYY') }}
                                    </td>
                                    <td>
                                        {{ advice.published ? t('general_text_yes') : t('general_text_no') }}
                                    </td>
                                    <td>{{ advice.sortOrder }}</td>
                                    <td class="text-center">
                                        <v-menu
                                            offset-y
                                            left
                                        >
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn
                                                    icon
                                                    v-bind="attrs"
                                                    v-on="on"
                                                >
                                                    <v-icon>mdi-dots-vertical</v-icon>
                                                </v-btn>
                                            </template>
                                            <v-list dense>
                                                <v-list-item
                                                    @click.prevent="edit(advice)"
                                                >
                                                    <v-list-item-title
                                                        class="customized-deep-primary--text"
                                                    >{{ t('general_action_edit') }}
                                                    </v-list-item-title>
                                                </v-list-item>
                                                <v-list-item
                                                    @click.prevent="suspend(advice)"
                                                >
                                                    <v-list-item-title
                                                        v-if="advice.published"
                                                        class="customized-deep-primary--text"
                                                    >{{ t('general_action_suspend') }}
                                                    </v-list-item-title>
                                                    <v-list-item-title
                                                        v-else
                                                        class="orange--text"
                                                    >{{ t('general_action_unsuspend') }}
                                                    </v-list-item-title>
                                                </v-list-item>
                                                <confirm-modal
                                                    :title="t('general_title_confirm_action')"
                                                    @agree="del(advice)"
                                                >
                                                    <template #default="{ on, attrs }">
                                                        <v-list-item
                                                            href
                                                            v-on="on"
                                                            v-bind="attrs"
                                                        >
                                                            <v-list-item-title
                                                                class="customized-deep-primary--text"
                                                            >{{ t('general_action_delete') }}</v-list-item-title>
                                                        </v-list-item>
                                                    </template>
                                                </confirm-modal>
                                            </v-list>
                                        </v-menu>
                                    </td>
                                </tr>
                            </template>
                            </tbody>
                        </template>
                    </card-collection>
                </v-tabs-items>
            </v-tabs>
        </v-layout>
    </div>
</template>

<script lang="ts">
    import { computed, defineComponent, onMounted, reactive, toRefs, watch } from '@vue/composition-api'
    import { useI18n } from 'vue-i18n-composable'
    import { useStore } from '@/store'
    import { useRouter } from '@/composition-api'
    import { useAdviceHttp } from '@/core/api/advices'
    import { AdviceCollection } from '@/store/modules/advice-collection'
    import { Advice } from '@/core/models/advice'
    import { AdviceSection } from '@/core/models/advice-section'
    import DatePickerModal from '@/components/DatePickerModal.vue'
    import CardCollection from '@/components/CardCollection.vue'
    import ConfirmModal from '@/components/ConfirmModal.vue'
    import SortField from '@/components/SortField.vue'

    export default defineComponent({

        components: {
            DatePickerModal,
            CardCollection,
            ConfirmModal,
            SortField,
        },

        /**
         * Component constructor.
         * @param props
         */
        setup(props) {
            const state = reactive({
                overlay: true,
                meta: undefined,
            })

            const queries = reactive({
                search: undefined,
                page: 1,
                per_page: 20,
                sort: '-id',
                published: '',
                date_from: undefined,
                date_to: undefined,
                advice_section_id: undefined,
                relations: 'section',
            })

            const reset = () => {
                queries.search = undefined
                queries.date_from = undefined
                queries.published = ''
                queries.date_to = undefined
                queries.advice_section_id = undefined
                capture()
            }

            const store = useStore()
            const router = useRouter()

            const collection = computed(() => <AdviceCollection>store.getters['adviceCollection/get'])
            const sections = computed(
                () => [{ text: '-', value: '' }].concat(
                    store.getters['adviceSectionCollection/get'].data.map((item: AdviceSection) => {
                        return { text: item.name, value: item.id.toString() }
                    })
                )
            )

            const updated = (updatedQueries: any) => {
                queries.page = updatedQueries.page
                capture()
            }

            const capture = () => {
                state.overlay = true
                store.dispatch('adviceCollection/get', queries).then((data) => {
                    state.meta = data.meta
                    state.overlay = false
                })
            }

            const process = () => {
                state.overlay = true
                useAdviceHttp().process().then((data: any) => {
                    state.overlay = false
                })
            }

            const touch = (advice: Advice) => {
                router.push({ name: 'advices.view', params: <any>{ id: advice.id } })
            }

            const edit = (advice: Advice) => {
                router.push({ name: 'advices.show', params: <any>{ id: advice.id } })
            }

            const suspend = (advice: Advice) => {
                state.overlay = true
                store.dispatch('advice/update', {
                    id: advice.id,
                    payload: { published: ! advice.published }
                }).then(() => {
                    capture()
                })
            }

            const del = (advice: Advice) => {
                state.overlay = true
                store.dispatch('advice/delete', { id: advice.id }).then(() => {
                    capture()
                })
            }

            const search = () => {
                capture()
            }

            const clear = () => {
                queries.search = undefined
                capture()
            }

            const onSort = (value: any) => {
                if (value === undefined) {
                    value = '-id'
                }
                queries.sort = value
            }

            watch(() => [
                queries.sort,
                queries.published,
                queries.advice_section_id,
            ], () => {
                capture()
            })

            onMounted(() => {
                collection.value.data = []
                store.dispatch('adviceSectionCollection/get', { sort: '-id' })
                capture()
            })

            return {
                queries,
                collection: collection.value,
                sections,
                updated,
                touch,
                edit,
                suspend,
                del,
                onSort,
                search,
                clear,
                reset,
                process,
                ...useI18n(),
                ...toRefs(state)
            }
        }
    })
</script>
